// SurveyConnectivityContact.tsx
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Form, notification } from 'antd';
import { useIntl } from 'react-intl';

import { IAppComponentProps } from '../../../components';
import { SurveyContactInput } from '../../../../__generated__/globalTypes';
import { getSurveyContactByType, updateSurveyContact } from './SurveyConnectivityService';
import { StateContainer } from '../utilities/StateContainer';
import { MODE } from '../IStoredSurvey';
import { useAppDispatch, useAppSelector } from '../../../main/hooks';
import { resetSurveyDataLoading, setSurveyDataLoading } from '../IStoredSurveySlice';
import { convertFromSpecifiedDateFormatToUserDefined } from '../../users/UserFormatPreferenceServices';
import { getI18n } from '../../../utils/Utils';

import SurveyConnectivityContactDescription from './SurveyConnectivityContactDescription';
import SurveyConnectivityContactForm from './SurveyConnectivityContactForm';
import { useMutation } from '@apollo/client';
import { MUTATE_DELETE_SURVEY_CONTACT_BY_TYPE } from '../queries';

const TECHNICAL_CONTACT_PRIMARY: number = 1;
const TECHNICAL_CONTACT_SECONDARY: number = 4;

type NotificationType = 'success' | 'info' | 'warning' | 'error';

type ISurveyConnectivityInfoContactProps = {
    groupId?: string;
    onStatusUpdate?: () => void;
    stateContainer?: StateContainer;
} & IAppComponentProps;

const SurveyConnectivityContact: FunctionComponent<ISurveyConnectivityInfoContactProps> = (props) => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const [form] = Form.useForm();
    const [contactInfo, setContactInfo] = useState<SurveyContactInput[] | undefined>(undefined);
    const [api, contextHolder] = notification.useNotification();

    const surveyState = useAppSelector((state) => state.survey);

    const isViewOrOverview = () =>
        surveyState.survey.surveyMode === MODE.VIEW || surveyState.survey.surveyMode === MODE.OVERVIEW;

    const formatContactModificationTimestamp = (timeStamp: string) => {
        if (!timeStamp) return timeStamp;
        return `${convertFromSpecifiedDateFormatToUserDefined(
            props.currentUser,
            timeStamp.substring(0, 10),
            'YYYY-MM-DD'
        )} ${timeStamp.substring(11)}`;
    };

    const showMessage = (type: NotificationType, headerMessage: string, description: string) => {
        notification[type]({
            message: headerMessage,
            description,
        });
    };

    const [deleteSecondContact] = useMutation(MUTATE_DELETE_SURVEY_CONTACT_BY_TYPE, {
        variables: {
            groupId: props.groupId,
            contactTypeId: TECHNICAL_CONTACT_SECONDARY,
        },
    });

    const loadSurveyContact = async (groupId: string, contactType: number) => {
        const response: any = await getSurveyContactByType(groupId, contactType);
        if (response.getSurveyContactByType?.length > 0) {
            return response.getSurveyContactByType.map((item) => ({
                contactFirstName: item.contactFirstName,
                contactLastName: item.contactLastName,
                contactNumber: item.contactNumber,
                contactEmailAddres: item.contactEmailAddres,
                updateTime: formatContactModificationTimestamp(item.updateTime),
            }));
        }
        return [];
    };

    const loadData = () => {
        dispatch(setSurveyDataLoading(true));
        const contactTypes = [TECHNICAL_CONTACT_PRIMARY, TECHNICAL_CONTACT_SECONDARY];
        Promise.all(contactTypes.map((type) => loadSurveyContact(props.groupId!, type)))
            .then((results) => {
                const contactInfos = results.flat();
                setContactInfo(contactInfos);
                form.setFieldsValue({ items: contactInfos.length > 0 ? contactInfos : [{}] });
            })
            .catch(() => dispatch(resetSurveyDataLoading(false)))
            .finally(() => dispatch(setSurveyDataLoading(false)));
    };

    const onDeleteSecondContact = () => {
        dispatch(setSurveyDataLoading(true));
        deleteSecondContact()
            .then(() => {
                showMessage(
                    'success',
                    'SUCCESSFULLY',
                    getI18n(
                        'survey-connectivity-delete-contact-success-message',
                        'Second Contact has been successfully deleted.',
                        intl
                    )
                );
                dispatch(setSurveyDataLoading(false));
            })
            .catch(() => {
                showMessage(
                    'error',
                    'ERROR',
                    getI18n(
                        'survey-connectivity-delete-contact-error-message',
                        'Error trying to delete second contact.',
                        intl
                    )
                );

                dispatch(setSurveyDataLoading(false));
            });
    };

    const onFinish = async (itemValues: any) => {
        dispatch(setSurveyDataLoading(true));
        try {
            const contactInfos = await Promise.all(
                itemValues.items.map((values: any, index: number) =>
                    updateSurveyContact(props.groupId!, {
                        contactEmailAddres:values.contactEmailAddres,
                        contactFirstName:values.contactFirstName,
                        contactLastName:values.contactLastName,
                        contactNumber:values.contactNumber,
                        contactTypeId: index === 0 ? TECHNICAL_CONTACT_PRIMARY : TECHNICAL_CONTACT_SECONDARY,
                        updateTime: null,
                    }).then((response) => ({
                        ...response.updateSurveyContact,
                        updateTime: formatContactModificationTimestamp(response.updateSurveyContact.updateTime),
                    }))
                )
            );
            showMessage(
                'success',
                'SUCCESSFULLY',
                getI18n(
                    'survey-connectivity-save-contact-success-message',
                    'Connectivity Information saved successfully',
                    intl
                )
            );

            setContactInfo(contactInfos);
            form.setFieldsValue({ items: contactInfos });
            props.onStatusUpdate?.();
        } catch {
            showMessage(
                'error',
                'ERROR',
                getI18n(
                    'survey-connectivity-save-contact-failed-message',
                    'Failed to save connectivity information',
                    intl
                )
            );
        } finally {
            dispatch(setSurveyDataLoading(false));
        }
    };

    useEffect(() => {
        if (contactInfo === undefined) loadData();
    }, [contactInfo]);

    return (
        <>
            <SurveyConnectivityContactDescription groupId={props.groupId} />
            <SurveyConnectivityContactForm
                form={form}
                contactInfo={contactInfo}
                onFinish={onFinish}
                onDeleteSecondContact={onDeleteSecondContact}
                isReadOnly={isViewOrOverview()}
            />
        </>
    );
};

export default SurveyConnectivityContact;
